import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import ImageIcon from '@material-ui/icons/Image'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Button,
  Dropzone,
  DEFAULT_FILE_ACCEPT,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  SearchBar,
  Spinner,
  FilePreview,
  useDebounce,
  useIsMobile,
  makeStyles,
  imageCrossOrigin,
  retryCorsImageOnError,
  isPdfFile,
  useTranslation
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import type { WithClientOptions, WithUser } from '@paintscout/ui-business'
import { withClientOptions, withUser } from '@paintscout/ui-business'
import { getFeature, hasIntegrationInterface } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { HEIC_FILE_TYPES } from '@paintscout/util/s3'
import { useSnackbar } from 'notistack'
import type {
  AddFileDialogTab,
  CompanyCamApiPhoto,
  CompanyCamPhotoFile,
  CompanyCamPhotoTab,
  CompanyCamProject,
  CcToolbarActionKey
} from './AddFileDialog.helpers'
import {
  appendUniquePhotoFiles,
  buildAnnotatedPhotoFiles,
  buildCompanyCamPhotoFiles,
  DEFAULT_COMPANY_CAM_PHOTO_TAB,
  getCcEmptyStateKey,
  getCompanyCamToolbarActions,
  isAddFileDialogTab,
  isCompanyCamPhotoTab,
  orderPickedPhotos,
  parseAnnotatedFilesResult,
  readLastCcProject,
  readLastPhotoTab,
  readLastTab,
  writeLastCcProject,
  writeLastPhotoTab,
  writeLastTab
} from './AddFileDialog.helpers'
import type { EventError } from '../../util/integrationEventError'
import {
  extractEventError,
  getCaughtErrorMessage,
  getEventErrorMessage,
  getProviderDisplayName,
  isNotFoundEventError
} from '../../util/integrationEventError'
import { PROCESS_EVENT } from '../../util/processEvent'
import { useCompanyQuery, useContactQuery, useDealQuery } from '@paintscout/api'
import type { Company, Contact, Deal } from '@paintscout/api'
import type { QuoteFile } from 'paintscout'
import { useMutation } from '@apollo/react-hooks'

export type { AddFileDialogTab } from './AddFileDialog.helpers'

export interface AddFileDialogProps extends Omit<DialogProps, 'onClose' | 'title'> {
  /** Title shown at the top. Defaults to 'Add File'. */
  title?: string
  /** Optional subtitle under the title (e.g. 'Filter Primary'). */
  subtitle?: string

  /** Max file size for upload, in bytes. Defaults to 100MB. */
  maxFileSize?: number
  /** Accepted MIME types for upload. Defaults to images + PDF. */
  accept?: string

  /** Optional doc whose attached files populate the 'Deal Files' tab. */
  fileDocId?: string
  fileDocType?: 'contact' | 'company' | 'deal'
  /**
   * Scopes the remembered CompanyCam project to a single document — pass the
   * quote `_id` (estimate flows) or the deal `_id` (deal flows). With it set,
   * re-opening the picker on the same doc jumps back to its last project, while
   * a different (or new) doc opens the general project search. Omit it (note
   * flows) to fall back to the legacy per-user memory.
   */
  scopeId?: string
  /**
   * Pre-fetched files to populate the source tab directly, instead of querying a
   * single doc via fileDocId/fileDocType. Used to pull from linked estimates into
   * a deal (the reverse of the deal -> estimate flow). When provided, takes
   * precedence over the queried doc files.
   */
  sourceFiles?: QuoteFile[]
  /** Override the 'Deal Files' tab label (defaults to 'Deal Files'). */
  dealFilesLabel?: string

  /** Tab to open initially. Defaults to 'upload'. */
  initialTab?: AddFileDialogTab

  /** Called with raw File objects when the user uploads via the Upload tab. */
  onUpload?: (files: File[]) => void

  /**
   * Called with existing QuoteFiles when the user picks from a source tab
   * (Deal Files or Company Cam). Company Cam files carry `provider: 'companycam'`
   * so the editor can disable in-app image editing for live remote URLs.
   */
  onSelectFiles?: (files: QuoteFile[]) => void

  /** Close handler — fires on X click, Cancel button, or backdrop dismissal. */
  onCancel?: () => void
}

type AddFileDialogPropsWithInjections = AddFileDialogProps & WithClientOptions & WithUser

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 100 // 100MB
const DEFAULT_ACCEPT = DEFAULT_FILE_ACCEPT

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    height: 'calc(100vh - 96px)',
    maxHeight: 720,
    width: '100%',
    maxWidth: 720,
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      maxHeight: '100%',
      maxWidth: '100%'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  titleSubtitle: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  content: {
    padding: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  tabsRow: {
    position: 'relative',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    // Draw the bottom divider as a 2px bar at bottom:0 — the same band the MUI active-tab
    // indicator occupies (height:2, bottom:0) — so the blue underline sits exactly on top
    // of the divider instead of floating above a 1px border (#21267). Mirrors the
    // NewFormSection tabs pattern used by the Availability settings tabs.
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '2px',
      background: theme.paintscout.palette.brand.Neutral.P90
    },
    // Raise the indicator span above the ::after divider so the active tab's blue line
    // wins where the two overlap.
    '& div > span': {
      zIndex: 2
    }
  },
  // Force each tab to fill the 48px tab row. The @paintscout/ui Tab (CustomizedTab) sets
  // minHeight:0, so tabs are only as tall as their content and the @paintscout/ui Tabs root
  // (align-items:center) centers that short scroller inside MUI's min-height:48 row, leaving
  // a gap below the scroller that floats the indicator above the divider. minHeight:48 fills
  // the row (as the Availability tabs / NewFormSection do) so the indicator drops onto the
  // ::after divider (#21267).
  tab: {
    minHeight: 48
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    textTransform: 'none'
  },
  availabilityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.success?.main ?? '#22c55e',
    display: 'inline-block'
  },
  panel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column'
  },
  dropzoneRoot: {
    flex: 1,
    minHeight: 240,
    border: `1.5px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.paintscout.palette.brand.Neutral.P98 ?? theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P50,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main
    }
  },
  dropzonePreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dropzoneIcon: {
    fontSize: 36,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  dropzoneText: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  fileMeta: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    marginTop: theme.spacing(1)
  },
  filesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    flex: 1,
    alignContent: 'start'
  },
  fileTile: {
    position: 'relative',
    // padding-top = width keeps tiles square; aspect-ratio is unreliable when
    // the percentage-height image feeds back into auto-sized grid rows
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  fileTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  fileTileImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  tileFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Wrapper that lets a shared FilePreview fill the square tile so PDFs/videos
  // render a real thumbnail (first-page PNG / video frame) instead of bare
  // filename text (#21654).
  fileTilePreview: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  // Override FilePreview root's default 100px min-size + padding so it fills the tile.
  fileTilePreviewRoot: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    padding: 0
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  ccPanel: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  ccSearch: {
    marginBottom: theme.spacing(1)
  },
  ccSearchBarRoot: {
    marginTop: 0
  },
  ccSearchBarPaper: {
    padding: `8px 16px`
  },
  ccProjectList: {
    flex: 1,
    overflow: 'auto'
  },
  ccProjectThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing(2),
    background: theme.paintscout.palette.brand.Neutral.P96
  },
  ccProjectThumbPlaceholder: {
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing(2),
    background: theme.paintscout.palette.brand.Neutral.P98,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  ccProjectThumbPlaceholderIcon: {
    fontSize: 32,
    color: theme.paintscout.palette.brand.Neutral.P90
  },
  ccProjectHeader: {
    marginBottom: theme.spacing(1)
  },
  ccProjectHeaderName: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P40
  },
  ccProjectHeaderAddress: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  ccSubTabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Backstop for the overflow menu below: the actions collapse to a kebab under
    // xs, but a long translated label could still outgrow the row just above that
    // breakpoint. Wrapping to a second row beats overlapping the tabs, which is
    // what made the Original label unreadable (ENG-1286).
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  ccPhotoGrid: {
    flex: 1,
    overflow: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    alignContent: 'start',
    // small padding so the selected tile's box-shadow ring isn't clipped at the scroll edges
    padding: theme.spacing(0.5)
  },
  ccPhotoGridEmpty: {
    // Span every grid column so the empty-state message fills the dialog width
    // and stays centered, instead of wrapping inside a single ~120px grid cell (ENG-726).
    gridColumn: '1 / -1'
  },
  ccLoadMoreCell: {
    // Same reasoning as ccPhotoGridEmpty: with no photos yet (an annotated scan
    // that hasn't reached the marked-up photos) "Load more" would otherwise
    // render inside one ~120px cell beside the full-width empty message (#21756).
    gridColumn: '1 / -1'
  },
  ccAlert: {
    marginBottom: theme.spacing(1)
  },
  ccPhotoTile: {
    position: 'relative',
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  ccPhotoTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
  },
  ccPhotoImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  // Caption overlay on a picker tile — mirrors the FilesGridNext overlay so the
  // CompanyCam description is visible while selecting. The gradient + padding
  // live on this container; the clamp lives on the inner text span. Keeping the
  // padding OFF the -webkit-box element avoids the iOS Safari bug where bottom
  // padding makes the last clamped line render cut off mid-glyph (#21231).
  ccPhotoCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: theme.spacing(0.5, 0.75),
    color: theme.palette.common.white,
    background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)',
    // let clicks fall through to the tile button beneath
    pointerEvents: 'none'
  },
  ccPhotoCaptionText: {
    ...theme.typography.caption,
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  ccToolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}))

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024 * 1024))}GB`
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

function AddFileDialog(props: AddFileDialogPropsWithInjections) {
  const {
    title,
    subtitle,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    accept = DEFAULT_ACCEPT,
    fileDocId,
    fileDocType,
    scopeId,
    sourceFiles,
    dealFilesLabel,
    initialTab,
    onUpload,
    onSelectFiles,
    onCancel,
    clientOptions,
    user,
    ...dialogProps
  } = props

  const { __ } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const resolvedTitle = title ?? __('Add File')
  const resolvedDealFilesLabel = dealFilesLabel ?? __('Deal Files')

  const classes = useStyles({})
  const ccUserId = removeAuth0Prefix(user?.user?.user_id ?? '')

  // HEIC is only convertible via the S3 upload path (server-side heicConvert
  // task) — mirrors the same guard in uploadFile.ts. Blocking it here too gives
  // an immediate rejection in the picker instead of a round-trip failure.
  const s3ImageUploadEnabled = getFeature({ options: clientOptions.options, path: 's3ImageUpload.enabled' })

  const companyCamAvailable = useMemo(
    () => hasIntegrationInterface({ options: clientOptions.options, integration: 'companycam' }),
    [clientOptions.options]
  )
  const hasPrefetchedSource = !!sourceFiles
  const dealFilesAvailable = hasPrefetchedSource ? sourceFiles.length > 0 : !!fileDocId && !!fileDocType

  // An explicit `initialTab` (e.g. the CompanyCam swap/add-provider flows) wins;
  // otherwise reopen on the tab the user last used, falling back to Upload.
  const [currentTab, setCurrentTab] = useState<AddFileDialogTab>(() => initialTab ?? readLastTab(ccUserId) ?? 'upload')

  // Persist only user-initiated tab switches, so forced `initialTab` opens don't
  // pollute the remembered tab.
  const handleTabChange = useCallback(
    (tab: AddFileDialogTab) => {
      setCurrentTab(tab)
      writeLastTab(ccUserId, tab)
    },
    [ccUserId]
  )
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())

  const { data: contactData } = useContactQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'contact',
    fetchPolicy: 'cache-and-network'
  })
  const { data: companyData } = useCompanyQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'company',
    fetchPolicy: 'cache-and-network'
  })
  const { data: dealData } = useDealQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'deal',
    fetchPolicy: 'cache-and-network'
  })

  const sourceDoc: Contact | Company | Deal | null =
    (contactData?.contact as Contact) || (companyData?.company as Company) || (dealData?.deal as Deal) || null
  const docFiles: QuoteFile[] = useMemo(
    () => sourceFiles ?? (sourceDoc ? (Object.values(sourceDoc?.files ?? {}) as QuoteFile[]) : []),
    [sourceFiles, sourceDoc]
  )

  // If the initially-selected tab becomes unavailable as props change, fall back to Upload.
  useEffect(() => {
    if (currentTab === 'deal-files' && !dealFilesAvailable) setCurrentTab('upload')
    if (currentTab === 'company-cam' && !companyCamAvailable) setCurrentTab('upload')
  }, [currentTab, dealFilesAvailable, companyCamAvailable])

  // --- Company Cam picker state ---
  const [ccQuery, setCcQuery] = useState('')
  const ccDebouncedQuery = useDebounce(ccQuery, 250)
  const [ccProjects, setCcProjects] = useState<CompanyCamProject[]>([])
  const [ccSelectedProject, setCcSelectedProject] = useState<CompanyCamProject | null>(null)
  const [ccLoadingProjects, setCcLoadingProjects] = useState(false)
  const [ccLoadingOriginals, setCcLoadingOriginals] = useState(false)
  const [ccLoadingAnnotated, setCcLoadingAnnotated] = useState(false)
  const [ccOriginalPhotos, setCcOriginalPhotos] = useState<CompanyCamPhotoFile[]>([])
  const [ccAnnotatedPhotos, setCcAnnotatedPhotos] = useState<CompanyCamPhotoFile[]>([])
  // Project id whose annotated scan has already been kicked off, so entering the
  // tab twice doesn't scan again (and staying on Original never scans at all).
  const [ccAnnotatedLoadedFor, setCcAnnotatedLoadedFor] = useState<string | null>(null)
  // Project id whose scan failed. Kept separate from the guard above on purpose:
  // clearing the guard on failure would let the lazy-load effect re-fire itself
  // immediately and retry in a loop, so a retry has to come from a user gesture
  // (returning to the tab, or Refresh).
  const [ccAnnotatedFailedFor, setCcAnnotatedFailedFor] = useState<string | null>(null)
  // Reopen on the last-used photo tab; default to Annotated when nothing is
  // stored. Support traced the "my markups didn't sync" reports to contractors
  // never clicking over to the tab at all (ENG-950), and the tab now scans up to
  // 500 photos server-side (#21756), so it is far less likely to look bare than
  // when it only filtered the page it happened to have loaded. A user who picks
  // Original keeps Original — the stored choice still wins over this default.
  const [ccPhotoTab, setCcPhotoTab] = useState<CompanyCamPhotoTab>(
    () => readLastPhotoTab(ccUserId) ?? DEFAULT_COMPANY_CAM_PHOTO_TAB
  )

  // Persist user-initiated photo-tab switches so the choice survives backing out
  // to the project list and re-opening the dialog later.
  const handleCcPhotoTabChange = useCallback(
    (tab: CompanyCamPhotoTab) => {
      setCcPhotoTab(tab)
      writeLastPhotoTab(ccUserId, tab)
      // Coming back to a tab whose scan failed is an explicit "try again" — clear
      // the guard so the lazy-load effect fires once more.
      if (tab === 'annotated' && ccAnnotatedFailedFor && ccAnnotatedFailedFor === ccSelectedProject?.id) {
        setCcAnnotatedFailedFor(null)
        setCcAnnotatedLoadedFor(null)
      }
    },
    [ccUserId, ccAnnotatedFailedFor, ccSelectedProject]
  )
  const [ccSelectedPhotoKeys, setCcSelectedPhotoKeys] = useState<Set<string>>(() => new Set())
  // The Original tab pages one CompanyCam page at a time...
  const [ccOriginalPage, setCcOriginalPage] = useState(1)
  const [ccOriginalHasMore, setCcOriginalHasMore] = useState(false)
  // ...while the Annotated tab has its own cursor into a server-side scan: it
  // can't filter a single fetched page, because a marked-up photo may sit
  // hundreds of photos deep (#21756). `1` means "not scanned yet", `null` means
  // the project is exhausted.
  const [ccAnnotatedNextPage, setCcAnnotatedNextPage] = useState<number | null>(1)
  const [ccAnnotatedScanned, setCcAnnotatedScanned] = useState(0)
  const [ccCaptionedOnly, setCcCaptionedOnly] = useState(false)
  // Under xs the photo toolbar's three actions overlapped the Annotated/Original
  // tabs, hiding the Original label (ENG-1286). At iPad portrait (768px) the row
  // still fits, so only phones collapse to the overflow menu.
  const ccToolbarCompact = useIsMobile({ xs: true })
  const [ccMenuAnchor, setCcMenuAnchor] = useState<HTMLElement | null>(null)
  // Drop the anchor whenever its trigger button goes away — rotating past the
  // breakpoint, or backing out to the project list. Left set, the anchor would
  // still point at a detached button the next time the menu mounts, so it would
  // open in the wrong place.
  //
  // There is no frame where the menu renders against that detached element: the
  // Menu lives inside the `ccToolbarCompact` branch below, so it unmounts in the
  // same render that removes the trigger. This effect only guards the return
  // trip back into the compact branch.
  useEffect(() => {
    setCcMenuAnchor(null)
  }, [ccToolbarCompact, ccSelectedProject])
  const [ccError, setCcError] = useState<EventError | null>(null)
  // True when CompanyCam failed part-way through a scan, so what we're showing is
  // known-incomplete rather than "there are no markups".
  const [ccPartial, setCcPartial] = useState(false)
  // Generation counter for in-flight photo requests. An annotated scan takes
  // seconds, so "project A → Back → project B" could otherwise land A's photos
  // in B's grid.
  const ccRequestRef = useRef(0)
  // Guards a double-clicked "Load more" from appending the same page twice.
  const ccLoadMoreRef = useRef(false)
  // Guards the one-shot auto-restore of the last CompanyCam project.
  const ccRestoredRef = useRef(false)
  // Mark the one-shot auto-restore as resolved for this dialog session. Called
  // when a restore fires, when the user manually picks a project, and when they
  // back out — any user navigation must stop the restore effect from re-running
  // and snapping straight back to the remembered project (#21283 follow-up).
  const consumeRestore = useCallback(() => {
    ccRestoredRef.current = true
  }, [])

  const [processEvent] = useMutation<any>(PROCESS_EVENT)

  // Forget a remembered project CompanyCam says is gone, and drop back to the
  // project list — otherwise the picker restores the same dead project on every
  // open and shows a permanently empty grid (the 404s behind Sentry CORE-API-MJR).
  const forgetMissingCcProject = useCallback(() => {
    writeLastCcProject(ccUserId, scopeId, null)
    consumeRestore()
    setCcSelectedProject(null)
    setCcOriginalPhotos([])
    setCcAnnotatedPhotos([])
    setCcSelectedPhotoKeys(new Set())
  }, [ccUserId, scopeId, consumeRestore])

  // Every CompanyCam fetch handles a failure the same way: show it, and if the
  // provider says the thing is gone, forget the remembered project.
  const handleCcEventFailure = useCallback(
    (error: EventError, context: string) => {
      setCcError(error)
      if (isNotFoundEventError(error)) {
        forgetMissingCcProject()
        return
      }
      // A non-404 is a genuine failure worth seeing — the server captures its own
      // scan errors, but a network failure or an unexpected processEvent shape
      // only ever reaches this branch. (No-ops in painter-view, which has no Sentry.)
      Sentry.captureException(new Error(getEventErrorMessage(error) ?? 'CompanyCam request failed'), {
        tags: { source: `AddFileDialog#${context}`, provider: 'companycam' }
      })
    },
    [forgetMissingCcProject]
  )

  const searchCcProjects = useCallback(
    async (query: string) => {
      if (currentTab !== 'company-cam' || ccSelectedProject) return
      setCcLoadingProjects(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-projects', provider: 'companycam', async: false, params: { query } }
        })
        const error = extractEventError(data)
        if (error) {
          handleCcEventFailure(error, 'getProjects')
          setCcProjects([])
          return
        }
        // Keep a "that project is gone" notice: dropping back to the list
        // immediately refetches it, and clearing here would wipe the only
        // explanation the user gets for why they were sent back.
        setCcError((prev) => (isNotFoundEventError(prev) ? prev : null))
        setCcProjects((data?.processEvent?.result?.result ?? []) as CompanyCamProject[])
      } catch (err) {
        // Without this, a failed lookup was an unhandled rejection plus a
        // "No Company Cam projects found." that blamed the account.
        handleCcEventFailure(getCaughtErrorMessage(err), 'getProjects')
        setCcProjects([])
      } finally {
        setCcLoadingProjects(false)
      }
    },
    [currentTab, ccSelectedProject, processEvent, handleCcEventFailure]
  )

  // The Original tab: one CompanyCam page per request.
  const fetchCcOriginals = useCallback(
    async (projectId: string, page: number) => {
      const generation = ccRequestRef.current
      // Clear the previous complaint when a load STARTS, not when one succeeds:
      // the originals page and the (slower) annotated scan overlap, so a success
      // landing after a failure would otherwise wipe the failure's alert.
      setCcError(null)
      if (page === 1) setCcLoadingOriginals(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-files', provider: 'companycam', async: false, params: { projectId, page } }
        })
        // A newer project (or a Refresh) superseded this request — drop it rather
        // than merging photos from the project the user has already left.
        if (generation !== ccRequestRef.current) return

        const error = extractEventError(data)
        if (error) {
          handleCcEventFailure(error, 'getFiles')
          return
        }
        const results = (data?.processEvent?.result?.result ?? []) as CompanyCamApiPhoto[]
        setCcOriginalHasMore(results.length === 100)
        const { originals } = buildCompanyCamPhotoFiles(results)
        if (page === 1) {
          setCcOriginalPhotos(originals)
        } else {
          setCcOriginalPhotos((prev) => appendUniquePhotoFiles(prev, originals))
        }
      } catch (err) {
        if (generation === ccRequestRef.current) handleCcEventFailure(getCaughtErrorMessage(err), 'getFiles')
      } finally {
        if (page === 1) setCcLoadingOriginals(false)
      }
    },
    [processEvent, handleCcEventFailure]
  )

  // The Annotated tab: a server-side scan across several CompanyCam pages, which
  // hands back `nextPage` so "Load more" keeps digging (#21756).
  const fetchCcAnnotated = useCallback(
    async (projectId: string, page: number, opts?: { append?: boolean }) => {
      const append = !!opts?.append
      const generation = ccRequestRef.current
      // Same start-scoped clear as fetchCcOriginals (see the note there).
      setCcError(null)
      setCcLoadingAnnotated(!append)
      try {
        const { data } = await processEvent({
          variables: {
            type: 'get-annotated-files',
            provider: 'companycam',
            async: false,
            params: { projectId, page }
          }
        })
        if (generation !== ccRequestRef.current) return

        const error = extractEventError(data)
        if (error) {
          // Mark the scan as failed so returning to the tab retries it, without
          // the effect retrying itself in a loop.
          setCcAnnotatedFailedFor(projectId)
          handleCcEventFailure(error, 'getAnnotatedFiles')
          return
        }
        const { photos, nextPage, scanned } = parseAnnotatedFilesResult(data?.processEvent?.result?.result)
        const annotated = buildAnnotatedPhotoFiles(photos)
        setCcPartial(!!data?.processEvent?.result?.partial)
        setCcAnnotatedNextPage(nextPage)
        setCcAnnotatedScanned((prev) => (append ? prev + scanned : scanned))
        if (append) {
          setCcAnnotatedPhotos((prev) => appendUniquePhotoFiles(prev, annotated))
        } else {
          setCcAnnotatedPhotos(annotated)
        }
      } catch (err) {
        if (generation === ccRequestRef.current) {
          setCcAnnotatedFailedFor(projectId)
          handleCcEventFailure(getCaughtErrorMessage(err), 'getAnnotatedFiles')
        }
      } finally {
        if (!append) setCcLoadingAnnotated(false)
      }
    },
    [processEvent, handleCcEventFailure]
  )

  // Select a project and remember it for next time the picker opens. A manual
  // pick consumes the auto-restore (see consumeRestore) so a later back-out
  // doesn't re-select this project.
  const selectCcProject = useCallback(
    (project: CompanyCamProject) => {
      consumeRestore()
      setCcSelectedProject(project)
      writeLastCcProject(ccUserId, scopeId, project)
    },
    [ccUserId, scopeId, consumeRestore]
  )

  // On first entering the CompanyCam tab, jump straight to the last-used project
  // (if any). Only a restore that actually fires consumes the flag, so a tab
  // visit with nothing saved doesn't permanently block a later restore.
  useEffect(() => {
    if (currentTab !== 'company-cam' || ccRestoredRef.current || ccSelectedProject) return
    const last = readLastCcProject(ccUserId, scopeId)
    if (last) {
      consumeRestore()
      setCcSelectedProject(last)
    }
  }, [currentTab, ccUserId, scopeId, ccSelectedProject, consumeRestore])

  // Fetch projects when entering the tab or when search changes.
  useEffect(() => {
    if (currentTab === 'company-cam' && !ccSelectedProject) {
      searchCcProjects(ccDebouncedQuery)
    }
  }, [currentTab, ccSelectedProject, ccDebouncedQuery, searchCcProjects])

  // Fetch the first page of originals when a project is picked. The annotated
  // scan is deliberately NOT started here — it costs several CompanyCam requests,
  // so it waits until the Annotated tab is actually shown.
  useEffect(() => {
    if (ccSelectedProject) {
      ccRequestRef.current += 1
      setCcOriginalPage(1)
      setCcOriginalHasMore(false)
      setCcOriginalPhotos([])
      setCcAnnotatedPhotos([])
      setCcAnnotatedNextPage(1)
      setCcAnnotatedScanned(0)
      setCcAnnotatedLoadedFor(null)
      setCcAnnotatedFailedFor(null)
      setCcError(null)
      setCcPartial(false)
      fetchCcOriginals(ccSelectedProject.id, 1)
    }
  }, [ccSelectedProject, fetchCcOriginals])

  // Scan for annotated photos the first time the Annotated tab is shown for a
  // project (including when the remembered tab reopens straight onto it).
  useEffect(() => {
    if (!ccSelectedProject || ccPhotoTab !== 'annotated') return
    if (ccAnnotatedLoadedFor === ccSelectedProject.id) return
    setCcAnnotatedLoadedFor(ccSelectedProject.id)
    fetchCcAnnotated(ccSelectedProject.id, 1)
  }, [ccSelectedProject, ccPhotoTab, ccAnnotatedLoadedFor, fetchCcAnnotated])

  const handleCcBack = useCallback(() => {
    // Backing out consumes the auto-restore so the restore effect can't re-run
    // and snap us straight back to the remembered project (#21283 follow-up).
    consumeRestore()
    // Invalidate any in-flight photo request for the project we're leaving.
    ccRequestRef.current += 1
    setCcSelectedProject(null)
    setCcOriginalPhotos([])
    setCcAnnotatedPhotos([])
    setCcSelectedPhotoKeys(new Set())
    setCcOriginalPage(1)
    setCcOriginalHasMore(false)
    setCcAnnotatedNextPage(1)
    setCcAnnotatedScanned(0)
    setCcAnnotatedLoadedFor(null)
    setCcAnnotatedFailedFor(null)
    setCcError(null)
    setCcPartial(false)
    // Keep the current photo tab so re-entering a project stays on Annotated/Original.
  }, [consumeRestore])

  // Re-pull the active tab for the current project. CompanyCam renders a markup
  // asynchronously, so a photo annotated moments ago may not have carried an
  // annotation URI when we last asked.
  const handleCcRefresh = useCallback(() => {
    if (!ccSelectedProject) return
    ccRequestRef.current += 1
    setCcError(null)
    setCcPartial(false)
    if (ccPhotoTab === 'annotated') {
      setCcAnnotatedPhotos([])
      setCcAnnotatedNextPage(1)
      setCcAnnotatedScanned(0)
      setCcAnnotatedLoadedFor(ccSelectedProject.id)
      setCcAnnotatedFailedFor(null)
      fetchCcAnnotated(ccSelectedProject.id, 1)
    } else {
      setCcOriginalPhotos([])
      setCcOriginalPage(1)
      setCcOriginalHasMore(false)
      fetchCcOriginals(ccSelectedProject.id, 1)
    }
  }, [ccSelectedProject, ccPhotoTab, fetchCcAnnotated, fetchCcOriginals])

  const handleCcTogglePhoto = (file: CompanyCamPhotoFile) => {
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleCcSelectAllVisible = () => {
    const base = ccPhotoTab === 'annotated' ? ccAnnotatedPhotos : ccOriginalPhotos
    const visible = ccCaptionedOnly ? base.filter((f) => !!f.caption) : base
    const everySelected = visible.length > 0 && visible.every((f) => ccSelectedPhotoKeys.has(f.key))
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (everySelected) visible.forEach((f) => next.delete(f.key))
      else visible.forEach((f) => next.add(f.key))
      return next
    })
  }

  const handleCcLoadMore = async () => {
    if (!ccSelectedProject || ccLoadMoreRef.current) return
    // Each tab advances only its own cursor — the annotated scan may be pages
    // ahead of (or behind) the originals list.
    ccLoadMoreRef.current = true
    try {
      if (ccPhotoTab === 'annotated') {
        if (!ccAnnotatedNextPage) return
        await fetchCcAnnotated(ccSelectedProject.id, ccAnnotatedNextPage, { append: true })
      } else {
        if (!ccOriginalHasMore) return
        const nextPage = ccOriginalPage + 1
        setCcOriginalPage(nextPage)
        await fetchCcOriginals(ccSelectedProject.id, nextPage)
      }
    } finally {
      ccLoadMoreRef.current = false
    }
  }

  const handleCcConfirm = () => {
    if (!onSelectFiles) return
    // ccSelectedPhotoKeys is a Set (insertion-ordered) so picked files arrive in
    // the order the user clicked them rather than fixed grid order.
    const picked = orderPickedPhotos(ccSelectedPhotoKeys, [...ccAnnotatedPhotos, ...ccOriginalPhotos])
    if (!picked.length) return

    // CompanyCam descriptions arrive programmatically and bypass the editor's
    // `maxLength`. We deliberately keep the full text rather than truncating it —
    // an over-limit caption is instead flagged red and blocked at save time in the
    // file editor so the user shortens it themselves and no text is silently lost
    // (#21295 / ENG-731).
    const files = picked.map(({ _photoId: _id, ...file }) => file)

    onSelectFiles(files)
    setCcSelectedPhotoKeys(new Set())
  }

  const isHeicFile = (file: File) => HEIC_FILE_TYPES.includes(file.type.split('/')[1])

  const handleUploadDrop = (files: File[]) => {
    if (!files.length || !onUpload) return

    let allowedFiles = files
    if (!s3ImageUploadEnabled) {
      allowedFiles = files.filter((file) => !isHeicFile(file))
      if (allowedFiles.length !== files.length) {
        enqueueSnackbar(__('HEIC images are not supported.'), {
          variant: 'error'
        })
      }
    }

    if (!allowedFiles.length) return
    onUpload(allowedFiles)
  }

  const handleToggleSourceFile = (file: QuoteFile) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleConfirmSelection = () => {
    if (!onSelectFiles) return
    const picked = docFiles.filter((f) => selectedKeys.has(f.key))
    if (!picked.length) return
    onSelectFiles(picked)
    setSelectedKeys(new Set())
  }

  const renderTabLabel = (label: string) => (
    <span className={classes.tabLabel}>
      <span className={classes.availabilityDot} aria-hidden />
      {label}
    </span>
  )

  const renderUploadPanel = () => (
    <div className={classes.panel}>
      <Dropzone
        accept={accept}
        maxSize={maxFileSize}
        multiple
        onDrop={handleUploadDrop}
        className={classes.dropzoneRoot}
        shouldRenderPreview
        renderPreview={() => (
          <div className={classes.dropzonePreview}>
            <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
            <div className={classes.dropzoneText}>{__('Select or drag and drop')}</div>
          </div>
        )}
      />
      <div className={classes.fileMeta}>{__('Max file size %s', formatFileSize(maxFileSize))}</div>
    </div>
  )

  const renderDealFilesPanel = () => {
    if (!docFiles.length) {
      return (
        <div className={classes.panel}>
          <div className={classes.emptyState}>
            {hasPrefetchedSource
              ? __('No files to pull yet.')
              : __('No files attached to this %s yet.', fileDocType ?? __('record'))}
          </div>
        </div>
      )
    }
    return (
      <div className={classes.panel}>
        <div className={classes.filesGrid}>
          {docFiles.map((file) => {
            const isImage = file.type?.startsWith('image') || file.format?.startsWith('image')
            const isSelected = selectedKeys.has(file.key)
            return (
              <button
                key={file.key}
                type="button"
                className={`${classes.fileTile} ${isSelected ? classes.fileTileSelected : ''}`}
                onClick={() => handleToggleSourceFile(file)}
              >
                {isImage && file.src ? (
                  // Match the new file editor's CORS load so Safari keeps a
                  // single CORS-clean cache entry for this src.
                  <img
                    crossOrigin={imageCrossOrigin(file)}
                    src={file.src}
                    alt={file.title || ''}
                    className={classes.fileTileImg}
                    onError={retryCorsImageOnError}
                  />
                ) : (
                  // PDFs and videos (and any other non-image source) render a real
                  // thumbnail via the shared FilePreview — a PDF's first page or a
                  // Cloudinary video frame — instead of bare filename text (#21654).
                  // Mirrors FilesGridNext. selectedPages={[0]} keeps a multi-page PDF
                  // from stacking every page into the small tile; videoThumbnail draws
                  // a still frame rather than mounting a heavy <video>.
                  <span className={classes.fileTilePreview}>
                    <FilePreview
                      file={file}
                      videoThumbnail
                      selectedPages={isPdfFile(file) ? [0] : undefined}
                      classes={{ root: classes.fileTilePreviewRoot }}
                    />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderCompanyCamPanel = () => {
    const ccErrorMessage = getEventErrorMessage(ccError)

    if (!ccSelectedProject) {
      return (
        <div className={classes.ccPanel}>
          {ccErrorMessage && (
            <Alert
              className={classes.ccAlert}
              severity="error"
              onClose={() => setCcError(null)}
              content={__('Error — %s returned: %s', getProviderDisplayName('companycam', __), ccErrorMessage)}
            />
          )}
          <div className={classes.ccSearch}>
            <SearchBar
              classes={{ root: classes.ccSearchBarRoot, paper: classes.ccSearchBarPaper }}
              placeholder={__('Search Projects...')}
              onChange={setCcQuery}
              value={ccQuery}
            />
          </div>
          <div className={classes.ccProjectList}>
            {ccLoadingProjects ? (
              <Spinner padded />
            ) : ccProjects.length === 0 ? (
              // Only claim there are none when the lookup actually succeeded —
              // the Alert above covers the failure case.
              ccErrorMessage ? null : (
                <div className={classes.emptyState}>{__('No Company Cam projects found.')}</div>
              )
            ) : (
              ccProjects.map((project) => (
                <ListItem key={project.id} divider button onClick={() => selectCcProject(project)}>
                  {project.featuredImage ? (
                    <img src={project.featuredImage} alt="" className={classes.ccProjectThumb} />
                  ) : (
                    // Projects with no photos have no featured image — render a placeholder
                    // tile so the row stays aligned with projects that have a thumbnail (#21276).
                    <div className={classes.ccProjectThumbPlaceholder} aria-hidden>
                      <ImageIcon className={classes.ccProjectThumbPlaceholderIcon} />
                    </div>
                  )}
                  <ListItemText
                    primary={project.name ?? project.id}
                    secondary={project.address?.street_address_1 ?? ''}
                  />
                </ListItem>
              ))
            )}
          </div>
        </div>
      )
    }

    const isAnnotatedTab = ccPhotoTab === 'annotated'
    const visiblePhotos = isAnnotatedTab ? ccAnnotatedPhotos : ccOriginalPhotos
    const displayedPhotos = ccCaptionedOnly ? visiblePhotos.filter((f) => !!f.caption) : visiblePhotos
    const visibleAllSelected =
      displayedPhotos.length > 0 && displayedPhotos.every((f) => ccSelectedPhotoKeys.has(f.key))
    const loadingVisible = isAnnotatedTab ? ccLoadingAnnotated : ccLoadingOriginals
    const hasMoreVisible = isAnnotatedTab ? !!ccAnnotatedNextPage : ccOriginalHasMore
    const ccToolbarActions = getCompanyCamToolbarActions({
      captionedOnly: ccCaptionedOnly,
      loading: loadingVisible,
      hasPhotos: !!displayedPhotos.length,
      allSelected: visibleAllSelected,
      __
    })
    const handleCcToolbarAction = (key: CcToolbarActionKey) => {
      if (key === 'captioned-only') {
        // Deliberately leaves the menu open: the filter re-draws the grid behind
        // it, and closing would hide the result of the tap the user just made.
        setCcCaptionedOnly((v) => !v)
        return
      }
      setCcMenuAnchor(null)
      if (key === 'refresh') handleCcRefresh()
      else handleCcSelectAllVisible()
    }
    const emptyStateKey = getCcEmptyStateKey({
      tab: ccPhotoTab,
      captionedOnly: ccCaptionedOnly,
      scanned: ccAnnotatedScanned,
      hasMore: hasMoreVisible
    })
    // Say only what we actually know. The Annotated list is a bounded scan, so
    // "none in this project" is true only once it's exhausted — claiming it after
    // one page is what hid genuinely marked-up photos (#21756). And while an error
    // is on screen we know nothing about the project at all, so make no claim.
    const ccEmptyStateCopy: Record<typeof emptyStateKey, string> = {
      'no-annotated-yet': __(
        'No annotated photos in the first %s photos. Load more to keep looking.',
        ccAnnotatedScanned
      ),
      'no-annotated': __('No annotated photos in this project.'),
      'no-captioned-yet': __('No captioned photos loaded yet. Load more to keep looking.'),
      'no-captioned': __('No captioned photos in this project.'),
      'no-photos': __('No photos in this project.')
    }
    const ccEmptyStateMessage = ccErrorMessage
      ? __("Couldn't load photos from Company Cam.")
      : ccEmptyStateCopy[emptyStateKey]

    return (
      <div className={classes.ccPanel}>
        <div className={classes.ccProjectHeader}>
          <div className={classes.ccProjectHeaderName}>{ccSelectedProject.name ?? ccSelectedProject.id}</div>
          {ccSelectedProject.address?.street_address_1 && (
            <div className={classes.ccProjectHeaderAddress}>{ccSelectedProject.address.street_address_1}</div>
          )}
        </div>
        {ccErrorMessage ? (
          // Without this, a failed CompanyCam call was indistinguishable from an
          // empty project — the picker just said "no photos" (#21756).
          <Alert
            className={classes.ccAlert}
            severity="error"
            onClose={() => setCcError(null)}
            content={__('Error — %s returned: %s', getProviderDisplayName('companycam', __), ccErrorMessage)}
          />
        ) : (
          ccPartial && (
            <Alert
              className={classes.ccAlert}
              severity="warning"
              onClose={() => setCcPartial(false)}
              content={__(
                "Company Cam didn't return everything — some photos may be missing. Try Load more or Refresh."
              )}
            />
          )
        )}
        <div className={classes.ccSubTabsRow}>
          <Tabs
            value={ccPhotoTab}
            onChange={(_, value) => {
              // Validate rather than blind-cast: never persist an unexpected value
              // (e.g. from a future MUI change) to localStorage.
              if (isCompanyCamPhotoTab(value)) handleCcPhotoTabChange(value)
            }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="annotated" label={__('Annotated')} />
            <Tab value="original" label={__('Original')} />
          </Tabs>
          {/* Under xs the three actions cannot share a row with the tabs, so they
              collapse into one overflow menu. Both branches render from the same
              action list, so their labels and disabled rules cannot drift. */}
          {ccToolbarCompact ? (
            <>
              <IconButton
                aria-label={__('More actions')}
                aria-haspopup="menu"
                onClick={(event) => setCcMenuAnchor(event.currentTarget)}
              >
                <MoreHorizIcon />
              </IconButton>
              <Menu
                anchorEl={ccMenuAnchor}
                open={Boolean(ccMenuAnchor)}
                onClose={() => setCcMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                // MUI v4 ignores a vertical anchorOrigin unless getContentAnchorEl
                // is null; without it the menu snaps back over the trigger. The
                // prop is gone in v5 and must be dropped on that migration.
                getContentAnchorEl={null}
              >
                {ccToolbarActions.map((action) => (
                  <MenuItem
                    key={action.key}
                    selected={action.selected}
                    disabled={action.disabled}
                    icon={action.selected ? CheckIcon : undefined}
                    onClick={() => handleCcToolbarAction(action.key)}
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <div className={classes.ccToolbarActions}>
              {ccToolbarActions.map((action) => (
                <Button
                  key={action.key}
                  variant={action.selected ? 'contained' : 'outlined'}
                  color={action.selected ? 'primary' : 'default'}
                  size="small"
                  disabled={action.disabled}
                  onClick={() => handleCcToolbarAction(action.key)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
        {loadingVisible ? (
          <Spinner padded />
        ) : (
          <div className={classes.ccPhotoGrid}>
            {displayedPhotos.length === 0 ? (
              <div className={`${classes.emptyState} ${classes.ccPhotoGridEmpty}`}>{ccEmptyStateMessage}</div>
            ) : (
              displayedPhotos.map((file) => {
                const isSelected = ccSelectedPhotoKeys.has(file.key)
                return (
                  <button
                    key={file.key}
                    type="button"
                    className={`${classes.ccPhotoTile} ${isSelected ? classes.ccPhotoTileSelected : ''}`}
                    onClick={() => handleCcTogglePhoto(file)}
                  >
                    {file.src ? (
                      // CompanyCam photos are served from an external host that may
                      // not return CORS headers — keep these non-CORS (no crossOrigin)
                      // to match how provider files are displayed in the viewer.
                      <img src={file.src} alt={file.title ?? ''} className={classes.ccPhotoImg} />
                    ) : (
                      <span className={classes.tileFill}>
                        <FilePreview file={file} />
                      </span>
                    )}
                    {file.caption && (
                      // Show the CompanyCam description while selecting; clamped to
                      // 2 lines with the full text available on hover (#21212). The
                      // clamp lives on the inner span so the container's padding
                      // doesn't clip the last line on iOS Safari (#21231).
                      <span className={classes.ccPhotoCaption} title={file.caption}>
                        <span className={classes.ccPhotoCaptionText}>{file.caption}</span>
                      </span>
                    )}
                  </button>
                )
              })
            )}
            {hasMoreVisible && (
              // Spans the grid so it stays reachable (and obvious) even when the
              // annotated scan hasn't turned up a photo yet.
              <div className={classes.ccLoadMoreCell}>
                <Button variant="outlined" onClick={handleCcLoadMore} fullWidth>
                  {__('Load more')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const showDealFilesConfirm = currentTab === 'deal-files' && selectedKeys.size > 0
  const showCcPickerFooter = currentTab === 'company-cam' && !!ccSelectedProject

  return (
    <Dialog {...dialogProps} maxWidth="md" fullWidth onClose={onCancel} classes={{ paper: classes.paper }}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} aria-label={__('Close')}>
            <CloseIcon />
          </IconButton>
        }
      >
        <div className={classes.titleStack}>
          <span>{resolvedTitle}</span>
          {subtitle && <span className={classes.titleSubtitle}>{subtitle}</span>}
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <Tabs
          value={currentTab}
          onChange={(_, value) => {
            if (isAddFileDialogTab(value)) handleTabChange(value)
          }}
          classes={{ root: classes.tabsRow }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab classes={{ root: classes.tab }} value="upload" label={renderTabLabel(__('Upload'))} />
          {dealFilesAvailable && (
            <Tab classes={{ root: classes.tab }} value="deal-files" label={renderTabLabel(resolvedDealFilesLabel)} />
          )}
          {companyCamAvailable && (
            <Tab classes={{ root: classes.tab }} value="company-cam" label={renderTabLabel(__('Company Cam'))} />
          )}
        </Tabs>
        {currentTab === 'upload' && renderUploadPanel()}
        {currentTab === 'deal-files' && renderDealFilesPanel()}
        {currentTab === 'company-cam' && renderCompanyCamPanel()}
      </DialogContent>
      {showDealFilesConfirm && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={onCancel}>
              {__('Cancel')}
            </Button>
          }
        >
          <Button variant="contained" onClick={handleConfirmSelection}>
            {__('Add %d %s', selectedKeys.size, selectedKeys.size === 1 ? __('file') : __('files'))}
          </Button>
        </DialogActions>
      )}
      {showCcPickerFooter && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={handleCcBack}>
              {__('Back')}
            </Button>
          }
        >
          <Button variant="contained" onClick={handleCcConfirm} disabled={ccSelectedPhotoKeys.size === 0}>
            {ccSelectedPhotoKeys.size > 0 ? `${__('Confirm')} (${ccSelectedPhotoKeys.size})` : __('Confirm')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default withUser(withClientOptions(AddFileDialog))
